import { NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import type { OnInit } from '@angular/core';
import { SITE_NAME } from '@freelancer/config';
import { Tracking } from '@freelancer/tracking';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor } from '@freelancer/ui/button';
import { ButtonComponent } from '../../button/button.component';
import { PermissionType } from '../permissions.model';

@Component({
  standalone: true,
  template: `
    <div class="MainBody">
      <h2
        class="text-large mb-xsmall"
        i18n="Permission Request Main Body Title"
      >
        <ng-container [ngSwitch]="permission">
          <ng-container *ngSwitchCase="PermissionType.PUSH_NOTIFICATIONS">
            {{ siteName }} would like to send you notifications
          </ng-container>
          <ng-container *ngSwitchDefault>
            {{ siteName }} needs access to your {{ permission }}
          </ng-container>
        </ng-container>
      </h2>
      <div>
        <p
          class="mb-small"
          i18n="Permission Request explanation"
        >
          {{ reason }}
        </p>
      </div>
    </div>
    <div class="ActionContainer">
      <fl-button
        class="mr-small"
        i18n="Deny Permission button"
        [color]="ButtonColor.TEXT_BLUE"
        (click)="handleClick(false)"
      >
        No, thanks
      </fl-button>
      <fl-button
        i18n="Request Permission button"
        [color]="ButtonColor.SECONDARY"
        (click)="handleClick(true)"
      >
        Continue
      </fl-button>
    </div>
  `,
  styleUrls: ['./permissions-request-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSwitch, NgSwitchCase, NgSwitchDefault, ButtonComponent],
})
export class PermissionsRequestModalComponent implements OnInit {
  private modalRef =
    inject<ModalRef<PermissionsRequestModalComponent>>(ModalRef);
  private tracking = inject(Tracking);
  siteName = inject(SITE_NAME);

  ButtonColor = ButtonColor;
  PermissionType = PermissionType;
  storeType: string;

  @Input() permission: PermissionType;
  @Input() reason: string;

  ngOnInit(): void {
    // Track when the user opens the request modal, with what permission,
    // and why.
    this.tracking.track('element_view', {
      section: 'GeneralPermissions',
      subsection: 'PermissionsRequestModal',
      label: 'ShowPermissionsRequestModal',
      extra_params: {
        permission: this.permission,
        permission_reason: this.reason,
      },
    });
  }

  handleClick(request: boolean): void {
    // Track when the user opens the request modal, with what permission,
    // and why.
    this.tracking.track('user_action', {
      section: 'GeneralPermissions',
      subsection: 'PermissionsRequestModal',
      label: request ? 'ContinueToBrowserOrNative' : 'RejectFromApp',
      action: 'click',
      extra_params: {
        permission: this.permission,
        permission_reason: this.reason,
      },
    });
    this.modalRef.close(request);
  }
}
